import { BoardMeetingState } from "@boark/shared/types/board-meeting";
import { BoardMeetingCommentCreate } from "@boark/shared/types/board-meeting-comment-create";
import { BoardMeetingCommentUpdate } from "@boark/shared/types/board-meeting-comment-update";
import {
  BoardMeetingCommentWithCreatedBy,
  boardMeetingCommentWithCreatedBySchema,
} from "@boark/shared/types/board-meeting-comment-with-created-by";
import { BoardMeetingContentCreate } from "@boark/shared/types/board-meeting-content-create";
import { BoardMeetingContentUpdate } from "@boark/shared/types/board-meeting-content-update";
import { BoardMeetingCreate } from "@boark/shared/types/board-meeting-create";
import { BoardMeetingSectionCreate } from "@boark/shared/types/board-meeting-section-create";
import { BoardMeetingSectionUpdate } from "@boark/shared/types/board-meeting-section-update";
import { BoardMeetingUpdate } from "@boark/shared/types/board-meeting-update";
import {
  BoardMeetingWithDetails,
  boardMeetingWithDetailsSchema,
} from "@boark/shared/types/board-meeting-with-details";
import {
  BoardMeetingWithPeople,
  boardMeetingWithPeopleSchema,
} from "@boark/shared/types/board-meeting-with-people";
import { User } from "@boark/shared/types/user";
import * as z from "zod";

import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "../utils/request";
import { recordIdToId } from "../utils/record-id-to-id";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

function meetingUrl(meetingId: string, path = ""): string {
  return `${API_BASE_URL}/board-meeting/${recordIdToId(meetingId)}${path}`;
}

export function formatUserName(user: User): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name !== "" ? name : user.email;
}

// Path in file storage for attachments of a content
export function boardMeetingContentFilePath(
  meetingId: string,
  contentId: string,
): string {
  return `board-meeting/${recordIdToId(meetingId)}/${recordIdToId(contentId)}`;
}

// Converts between a Date and the value of a datetime-local input
export function toDateTimeLocalValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function fromDateTimeLocalValue(value: string): Date {
  return new Date(value);
}

export async function getBoardMeetings(): Promise<BoardMeetingWithPeople[]> {
  return getRequest(
    `${API_BASE_URL}/board-meeting`,
    z.array(boardMeetingWithPeopleSchema),
  );
}

export async function getBoardMeeting(
  meetingId: string,
): Promise<BoardMeetingWithDetails> {
  return getRequest(meetingUrl(meetingId), boardMeetingWithDetailsSchema);
}

export async function createBoardMeeting(
  meeting: BoardMeetingCreate,
): Promise<BoardMeetingWithDetails> {
  return postRequest(
    `${API_BASE_URL}/board-meeting`,
    meeting,
    boardMeetingWithDetailsSchema,
  );
}

export async function updateBoardMeeting(
  meetingId: string,
  meeting: BoardMeetingUpdate,
): Promise<BoardMeetingWithDetails> {
  return putRequest(
    meetingUrl(meetingId),
    meeting,
    boardMeetingWithDetailsSchema,
  );
}

export async function changeBoardMeetingState(
  meetingId: string,
  state: BoardMeetingState,
): Promise<BoardMeetingWithDetails> {
  return putRequest(
    meetingUrl(meetingId, "/state"),
    { state },
    boardMeetingWithDetailsSchema,
  );
}

export async function setBoardMeetingConfirmation(
  meetingId: string,
  confirmed: boolean,
): Promise<BoardMeetingWithDetails> {
  const url = meetingUrl(meetingId, "/confirmation");
  return confirmed
    ? postRequest(url, {}, boardMeetingWithDetailsSchema)
    : deleteRequest(url, boardMeetingWithDetailsSchema);
}

export async function deleteBoardMeeting(meetingId: string): Promise<void> {
  return deleteRequest(meetingUrl(meetingId));
}

export async function createBoardMeetingSection(
  meetingId: string,
  section: BoardMeetingSectionCreate,
): Promise<BoardMeetingWithDetails> {
  return postRequest(
    meetingUrl(meetingId, "/section"),
    section,
    boardMeetingWithDetailsSchema,
  );
}

export async function updateBoardMeetingSection(
  meetingId: string,
  sectionId: string,
  section: BoardMeetingSectionUpdate,
): Promise<BoardMeetingWithDetails> {
  return putRequest(
    meetingUrl(meetingId, `/section/${recordIdToId(sectionId)}`),
    section,
    boardMeetingWithDetailsSchema,
  );
}

export async function deleteBoardMeetingSection(
  meetingId: string,
  sectionId: string,
): Promise<BoardMeetingWithDetails> {
  return deleteRequest(
    meetingUrl(meetingId, `/section/${recordIdToId(sectionId)}`),
    boardMeetingWithDetailsSchema,
  );
}

export async function reorderBoardMeetingSections(
  meetingId: string,
  ids: string[],
): Promise<BoardMeetingWithDetails> {
  return putRequest(
    meetingUrl(meetingId, "/section-order"),
    { ids },
    boardMeetingWithDetailsSchema,
  );
}

export async function createBoardMeetingContent(
  meetingId: string,
  sectionId: string,
  content: BoardMeetingContentCreate,
): Promise<BoardMeetingWithDetails> {
  return postRequest(
    meetingUrl(meetingId, `/section/${recordIdToId(sectionId)}/content`),
    content,
    boardMeetingWithDetailsSchema,
  );
}

export async function updateBoardMeetingContent(
  meetingId: string,
  contentId: string,
  content: BoardMeetingContentUpdate,
): Promise<BoardMeetingWithDetails> {
  return putRequest(
    meetingUrl(meetingId, `/content/${recordIdToId(contentId)}`),
    content,
    boardMeetingWithDetailsSchema,
  );
}

export async function deleteBoardMeetingContent(
  meetingId: string,
  contentId: string,
): Promise<BoardMeetingWithDetails> {
  return deleteRequest(
    meetingUrl(meetingId, `/content/${recordIdToId(contentId)}`),
    boardMeetingWithDetailsSchema,
  );
}

export async function reorderBoardMeetingContents(
  meetingId: string,
  sectionId: string,
  ids: string[],
): Promise<BoardMeetingWithDetails> {
  return putRequest(
    meetingUrl(meetingId, `/section/${recordIdToId(sectionId)}/content-order`),
    { ids },
    boardMeetingWithDetailsSchema,
  );
}

export async function getBoardMeetingComments(
  meetingId: string,
): Promise<BoardMeetingCommentWithCreatedBy[]> {
  return getRequest(
    meetingUrl(meetingId, "/comment"),
    z.array(boardMeetingCommentWithCreatedBySchema),
  );
}

export async function createBoardMeetingComment(
  meetingId: string,
  contentId: string,
  comment: BoardMeetingCommentCreate,
): Promise<BoardMeetingCommentWithCreatedBy> {
  return postRequest(
    meetingUrl(meetingId, `/content/${recordIdToId(contentId)}/comment`),
    comment,
    boardMeetingCommentWithCreatedBySchema,
  );
}

export async function updateBoardMeetingComment(
  meetingId: string,
  commentId: string,
  comment: BoardMeetingCommentUpdate,
): Promise<BoardMeetingCommentWithCreatedBy> {
  return putRequest(
    meetingUrl(meetingId, `/comment/${recordIdToId(commentId)}`),
    comment,
    boardMeetingCommentWithCreatedBySchema,
  );
}

export async function deleteBoardMeetingComment(
  meetingId: string,
  commentId: string,
): Promise<void> {
  return deleteRequest(
    meetingUrl(meetingId, `/comment/${recordIdToId(commentId)}`),
  );
}
